import { differenceInDays } from 'date-fns';
import { makeAutoObservable } from 'mobx';

import { getGeosByIds } from 'api/geo';
import { geoSuggestionTypes } from 'constants/geo';
import { SiteFeaturesIDs } from 'store/SiteFeatures/SiteFeatures';
import { atiConfBannerClosedTimestampStorage } from 'utils/storage/Storage';

import { RootStore } from '../RootStore';

const repeatShowIntervalDays = 7;

const firmTypeIds = {
  carrier: 1,
  forwarder: 2,
  forwarderCarrier: 5,
};

const federalDistrictIds = {
  central: 1,
  northwestern: 2,
};

const targetFirmTypeIds = [firmTypeIds.carrier, firmTypeIds.forwarder, firmTypeIds.forwarderCarrier];

const targetFederalDistrictIds = [federalDistrictIds.central, federalDistrictIds.northwestern];

type TCityByIds = {
  federal_district_id?: number;
};

class AtiConfPromoStore {
  root;
  bannerClosedTimestamp: number = atiConfBannerClosedTimestampStorage.get();
  isFirmInTargetDistrict: boolean | null = null;

  constructor(root: RootStore) {
    this.root = root;

    makeAutoObservable(this);
  }

  get isFeatureEnabled() {
    return Boolean(this.root.siteFeatures.isSiteFeatureEnabled[SiteFeaturesIDs.atiConfBanner]);
  }

  get isTargetFirmType() {
    const firmTypeId = this.root.profile.data?.profile?.firmTypeId;

    return firmTypeId !== undefined && targetFirmTypeIds.includes(firmTypeId);
  }

  get isBannerDismissed() {
    if (!this.bannerClosedTimestamp) return false;

    return differenceInDays(Date.now(), this.bannerClosedTimestamp) < repeatShowIntervalDays;
  }

  get canShowBanner() {
    return this.isFeatureEnabled && this.isTargetFirmType && !this.isBannerDismissed;
  }

  get isBannerVisible() {
    return this.canShowBanner && this.isFirmInTargetDistrict === true;
  }

  closeBanner = () => {
    this.bannerClosedTimestamp = Date.now();
    atiConfBannerClosedTimestampStorage.set(this.bannerClosedTimestamp);
  };

  setIsFirmInTargetDistrict = (value: boolean) => {
    this.isFirmInTargetDistrict = value;
  };

  fetchFirmFederalDistrict = async () => {
    const cityId = this.root.profile.data?.profile?.cityId;

    if (!this.canShowBanner || !cityId) return;

    try {
      const { data } = await getGeosByIds([String(cityId)], geoSuggestionTypes.city);
      const [city]: TCityByIds[] = data.cities;

      this.setIsFirmInTargetDistrict(
        city?.federal_district_id !== undefined && targetFederalDistrictIds.includes(city.federal_district_id),
      );
    } catch (error) {
      console.error(error);
    }
  };
}

export { AtiConfPromoStore };
